import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { CardModule } from 'primeng/card';

/**
 * Interface for dashboard statistics.
 */
interface DashboardStat {
  title: string;
  value: number;
  icon: string;
  iconColor: string;
  bgColor: string;
  borderColor: string;
}

@Component({
  selector: 'app-dashboard-page',
  imports: [CommonModule, TranslocoPipe, CardModule],
  template: `
    <div class="p-4">
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-surface-900 dark:text-surface-0">
          {{ 'dashboard.title' | transloco }}
        </h1>
        <p class="mt-2 text-surface-600 dark:text-surface-400">
          {{ 'dashboard.subtitle' | transloco }}
        </p>
      </div>

      <!-- Stats Cards Grid -->
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        @for (stat of stats(); track stat.title) {
          <div class="h-full">
            <p-card
              [styleClass]="'h-full border-l-4 shadow-lg ' + stat.borderColor"
            >
              <div class="flex items-center justify-between p-2">
                <div class="flex-1">
                  <p class="mb-2 text-sm font-semibold uppercase tracking-wide text-surface-600 dark:text-surface-400">
                    {{ stat.title | transloco }}
                  </p>
                  <p class="text-4xl font-bold text-surface-900 dark:text-surface-0">
                    {{ stat.value }}
                  </p>
                </div>
                <div
                  class="flex h-20 w-20 items-center justify-center rounded-full shadow-md"
                  [ngClass]="stat.bgColor"
                >
                  <i [class]="stat.icon + ' text-3xl'" [ngClass]="stat.iconColor"></i>
                </div>
              </div>
            </p-card>
          </div>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Dashboard page component displaying key operational metrics.
 *
 * Displays statistics for:
 * - Today's deliveries
 * - Today's pickups
 * - Trucks currently en route
 * - Containers in front of warehouses
 */
export class DashboardComponent {
  // Mock data for dashboard statistics
  private readonly mockDeliveries = signal(12);
  private readonly mockPickups = signal(8);
  private readonly mockTrucksEnRoute = signal(5);
  private readonly mockContainers = signal(23);

  /** Computed array of all dashboard statistics. */
  stats = computed<DashboardStat[]>(() => [
    {
      title: 'dashboard.stats.todaysDeliveries',
      value: this.mockDeliveries(),
      icon: 'pi pi-box',
      iconColor: 'text-green-700',
      bgColor: 'bg-green-100 dark:bg-green-900/20',
      borderColor: 'border-green-500',
    },
    {
      title: 'dashboard.stats.todaysPickups',
      value: this.mockPickups(),
      icon: 'pi pi-sign-in',
      iconColor: 'text-blue-700',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
      borderColor: 'border-blue-500',
    },
    {
      title: 'dashboard.stats.trucksEnRoute',
      value: this.mockTrucksEnRoute(),
      icon: 'pi pi-truck',
      iconColor: 'text-orange-700',
      bgColor: 'bg-orange-100 dark:bg-orange-900/20',
      borderColor: 'border-orange-500',
    },
    {
      title: 'dashboard.stats.containersAtWarehouse',
      value: this.mockContainers(),
      icon: 'pi pi-building',
      iconColor: 'text-purple-700',
      bgColor: 'bg-purple-100 dark:bg-purple-900/20',
      borderColor: 'border-purple-500',
    },
  ]);
}
